// src/pages/TrackCreation/CategoryScreen.tsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CategoryScreenProps, DropdownOption } from "../../../../types/screensTypes";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import Dropdown from "../../../../components/common/Dropdown";
import Button from "../../../../components/ui/Button";
import { getAdminInfo } from "../../../../api";
import { getCurrentVariant, getVariantOverride } from "../../../../utils/variantOverrides";
import stepStyles from "../createStep.module.css";

// Category IDs
const WALK_CATEGORY_ID = "iduniquewalk";
const MUSEUM_CATEGORY_ID = "iduniquemusee";
const BIKE_CATEGORY_ID = "iduniquebike";
const CAR_CATEGORY_ID = "iduniquecar";
const BUS_CATEGORY_ID = "iduniquebus";

// Special filter IDs
const FREE_WALKING_TOUR_FILTER_ID = "cmcj0hywj00013ic0ytonbe7q";

/** Image-scan filters on Rewind CMS only: gated to Rewind owners (Thoiry unchanged) */
const REWIND_COMPANY_ID = "rewinduniqueid";

const CategoryScreen: React.FC<CategoryScreenProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { data, loading, setToken } = useGlobalState();
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const isThoiry = useMemo(() => getCurrentVariant() === "thoiry", []);

    // Variant-aware translation: checks overrides first, then falls back to i18n
    const tv = useCallback(
        (key: string, options?: any) => {
            const override = getVariantOverride(key, i18n.language);
            return override ?? t(key, options);
        },
        [t, i18n.language],
    );

    const [transportMode, setTransportMode] = useState<string | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [canSelectImageScanFilterOnRewind, setCanSelectImageScanFilterOnRewind] = useState<boolean>(false);
    // getAdminInfo is async: until it settles, the image-scan gate is unknown and the
    // filter list is incomplete. Auto-selection must wait, or it locks in the wrong
    // filter for a Rewind owner on a category that has one scan filter and one other.
    const [adminInfoLoaded, setAdminInfoLoaded] = useState<boolean>(false);

    if (loading) {
        const token = localStorage.getItem("token");
        if (token) {
            setToken(token);
        }
    }

    useEffect(() => {
        // Thoiry keeps image-scan filters for all eligible users — no Rewind POC gate
        if (isThoiry) {
            setCanSelectImageScanFilterOnRewind(false);
            setAdminInfoLoaded(true);
            return;
        }
        const token = localStorage.getItem("token") || "";
        if (!token) {
            setAdminInfoLoaded(true);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const adminInfo = await getAdminInfo({ token });
                if (cancelled) return;
                const isOwner = adminInfo?.isOwner === true;
                const isRewind = adminInfo?.cmpany?.id === REWIND_COMPANY_ID;
                setCanSelectImageScanFilterOnRewind(isOwner && isRewind);
            } catch (error) {
                console.error("Error checking image-scan filter access:", error);
                if (!cancelled) setCanSelectImageScanFilterOnRewind(false);
            } finally {
                if (!cancelled) setAdminInfoLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isThoiry]);

    const languagesAIJsonFormatted: DropdownOption[] | undefined = data?.languagesAI?.map((language) => ({
        value: language.code,
        label: language.label,
    }));

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    // Map category IDs to custom labels (variant-aware for Thoiry overrides)
    const categoryLabelMap: Record<string, string> = {
        [WALK_CATEGORY_ID]: tv("transportModeWalkOutdoor"),
        [MUSEUM_CATEGORY_ID]: tv("transportModeWalkIndoor"),
        [BIKE_CATEGORY_ID]: tv("transportModeBike"),
        [CAR_CATEGORY_ID]: tv("transportModeCar"),
    };

    // Build transport mode options from actual database categories
    const transportModeOptions = useMemo(() => {
        const options: DropdownOption[] = [];

        // Add categories that exist in the database (except bus which is handled separately)
        if (data?.categories) {
            data.categories.forEach((category) => {
                // Skip bus category - its filters will be added individually
                if (category.value === BUS_CATEGORY_ID) return;

                options.push({
                    value: category.value,
                    // Use custom label if available, otherwise use database label
                    label: categoryLabelMap[category.value] || category.label,
                });
            });
        }

        // Add bus category filters as individual options
        if (data?.filtersAI) {
            const busFilters = data.filtersAI.filter((f) => f.categoryAI === BUS_CATEGORY_ID);
            busFilters.forEach((filter) => {
                options.push({
                    value: `bus_filter_${filter.value}`,
                    label: filter.label,
                });
            });
        }

        return options;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.categories, data?.filtersAI, tv]);

    // Check if selected transport mode is a category (not a bus filter)
    const isCategory = useMemo(() => {
        if (!transportMode) return false;
        // Check if it's a bus filter (not a category)
        if (transportMode.startsWith("bus_filter_")) return false;
        // Check if it exists in the categories from database
        return data?.categories?.some((cat) => cat.value === transportMode) ?? false;
    }, [transportMode, data?.categories]);

    // Check if selected transport mode is a bus filter
    const isBusFilter = useMemo(() => {
        return transportMode?.startsWith("bus_filter_") ?? false;
    }, [transportMode]);

    // Get filters for the selected category with customized labels
    const categoryFilters = useMemo(() => {
        if (!isCategory || !data?.filtersAI) return [];

        const filters = data.filtersAI.filter((f) => {
            if (f.categoryAI !== transportMode) return false;
            // Rewind CMS only: hide image-scan filters unless Rewind owner.
            // Thoiry keeps existing Horama / scan filters for everyone.
            if (f.hasOnlyImageScanWaypoints && !isThoiry && !canSelectImageScanFilterOnRewind) {
                return false;
            }
            return true;
        });

        // Customize labels based on category and filter
        return filters.map((filter) => {
            let customLabel = filter.label;

            // Keep DB / FilterLocalized name for image-scan filters (Thoiry Scan IA, Rewind Weaviate, etc.)
            if (!filter.hasOnlyImageScanWaypoints) {
                if (transportMode === MUSEUM_CATEGORY_ID) {
                    customLabel = t("filterLabelAudioguidePrefix");
                } else if (transportMode === WALK_CATEGORY_ID) {
                    // Walk filters: special handling
                    if (filter.value === FREE_WALKING_TOUR_FILTER_ID) {
                        customLabel = t("filterLabelAudioguideFreeWalking");
                    } else {
                        customLabel = t("filterLabelAudioguide");
                    }
                }
            }

            return {
                ...filter,
                label: customLabel,
            };
        });
    }, [isCategory, transportMode, data?.filtersAI, t, isThoiry, canSelectImageScanFilterOnRewind]);

    // Check if selected filter has scan image waypoints
    const selectedFilterData = useMemo(() => {
        if (!selectedFilter || !data?.filtersAI) return null;
        return data.filtersAI.find((f) => f.value === selectedFilter);
    }, [selectedFilter, data?.filtersAI]);

    const isScanFilter = selectedFilterData?.hasOnlyImageScanWaypoints ?? false;

    // Should show trigger mode (when filter is selected and it's not a scan filter)
    const shouldShowTriggerMode = useMemo(() => {
        if (!selectedFilter) return false;
        if (isScanFilter) return false;
        // Don't show for museum type
        if (transportMode === MUSEUM_CATEGORY_ID) return false;
        // Don't show for bike - it's always GPSAlert (semi-auto)
        if (transportMode === BIKE_CATEGORY_ID) return false;
        return true;
    }, [selectedFilter, isScanFilter, transportMode]);

    // Check if there's only one filter (should auto-select)
    const hasOnlyOneFilter = categoryFilters.length === 1;

    // Handle transport mode change
    const handleTransportModeChange = (value: string) => {
        setTransportMode(value);
        setSelectedFilter(null);

        // Reset dependent fields (default to progression enabled)
        updateTrackData("hasProgressionSystem", true);
        updateTrackData("defaultTriggerMode", undefined);

        if (value.startsWith("bus_filter_")) {
            // It's a bus filter - set type to bus and subType to the filter
            const filterId = value.replace("bus_filter_", "");
            updateTrackData("type", BUS_CATEGORY_ID);
            updateTrackData("subType", filterId);
        } else {
            // It's a category - set type, clear subType
            updateTrackData("type", value);
            updateTrackData("subType", undefined);

            // For bike category, force GPSAlert trigger mode
            if (value === BIKE_CATEGORY_ID) {
                updateTrackData("defaultTriggerMode", "GPSAlert");
            }
        }
    };

    // Auto-select filter when there's only one available
    useEffect(() => {
        if (adminInfoLoaded && isCategory && categoryFilters.length === 1 && !selectedFilter && !trackData.title) {
            const singleFilter = categoryFilters[0];
            setSelectedFilter(singleFilter.value);
            updateTrackData("subType", singleFilter.value);

            // For bike category, ensure GPSAlert is set
            if (transportMode === BIKE_CATEGORY_ID) {
                updateTrackData("defaultTriggerMode", "GPSAlert");
            }
        }
    }, [adminInfoLoaded, isCategory, categoryFilters, selectedFilter, transportMode, trackData.title, updateTrackData]);

    // Handle filter selection (Step 3)
    const handleFilterChange = (value: string) => {
        setSelectedFilter(value);
        updateTrackData("subType", value);

        // Reset progression when changing filter (default to progression enabled)
        updateTrackData("hasProgressionSystem", true);

        // Check if this is an image scan filter
        const filterData = data?.filtersAI?.find((f) => f.value === value);
        if (filterData?.hasOnlyImageScanWaypoints) {
            // Set IMAGE_SCAN trigger mode for image scan filters
            updateTrackData("defaultTriggerMode", "IMAGE_SCAN");
        } else {
            // Reset trigger mode for other filters
            updateTrackData("defaultTriggerMode", undefined);
        }
    };

    // Initialize state from existing trackData
    useEffect(() => {
        if (trackData) {
            setSelectedLanguage(trackData.language || null);

            // Reconstruct transportMode from trackData
            if (trackData.type === BUS_CATEGORY_ID && trackData.subType) {
                setTransportMode(`bus_filter_${trackData.subType}`);
            } else if (trackData.type) {
                setTransportMode(trackData.type);
            }

            // Set selected filter if it exists and it's a category type
            if (trackData.subType && trackData.type !== BUS_CATEGORY_ID) {
                setSelectedFilter(trackData.subType);
            }
        }
    }, [trackData]);

    const [fieldErrors, setFieldErrors] = useState<{
        selectedLanguage: boolean;
        transportMode: boolean;
        filter: boolean;
    }>({
        selectedLanguage: false,
        transportMode: false,
        filter: false,
    });

    useEffect(() => {
        setFieldErrors({
            selectedLanguage: !selectedLanguage,
            transportMode: !transportMode,
            filter: isCategory && categoryFilters.length > 0 ? !selectedFilter : false,
        });
    }, [selectedLanguage, transportMode, selectedFilter, isCategory, categoryFilters]);

    // Progression system options
    const progressionOptions: DropdownOption[] = [
        { value: "with", label: t("withProgression") },
        { value: "without", label: t("withoutProgression") },
    ];

    // Trigger mode options
    const triggerModeOptions: DropdownOption[] = [
        { value: "GPS", label: t("gpsFullAuto") },
        { value: "GPSAlert", label: t("gpsSemiAuto") },
    ];

    const buttonDisabled = (): boolean => {
        if (!trackData.language || !transportMode) return true;
        // If it's a category with filters, require a filter selection
        if (isCategory && categoryFilters.length > 0 && !selectedFilter) return true;
        return false;
    };

    // Once the experience has a title (saved past this step), category fields are locked.
    const isFrozen = Boolean(trackData.title);

    return (
        <div
            className={`step-container ${stepStyles.stepContainer} ${stepStyles.experienceCreate}${isFrozen ? ` ${stepStyles.experienceCreateLocked}` : ""} ${stepStyles.step}`}
        >
            <TitleSubtitle
                title={t("createANewAudioTour")}
                subtitle={t("fillTheInformationInTheFollowingScreensToCreateYourTrack")}
                layout="create"
            />

            {/* Step 1: Language Selection */}
            <div className={stepStyles.stepDescription}>
                <h1 className={stepStyles.textWithSpanTitle}>
                    <span>1</span>
                    {t("audiotrackLanguages")}
                </h1>
                <h3>{t("whatLanguageWillYouUseToCreateYourAudioTour")}</h3>
                <p>{t("youWillWriteDownAllTheInformationAboutYourVisit")}</p>
                <Dropdown
                    label=""
                    value={trackData.language}
                    options={languagesAIJsonFormatted}
                    onChange={(value: string) => {
                        updateTrackData("language", value);
                        setSelectedLanguage(value);
                    }}
                    placeholder={t("selectALanguage")}
                    disabled={loading || isFrozen}
                    isMissing={showErrors && fieldErrors.selectedLanguage}
                    loading={loading}
                    error={false}
                    isWithoutMargin
                />
            </div>

            {/* Step 2: Transport Mode */}
            <div className={stepStyles.stepDescription}>
                <h1 className={stepStyles.textWithSpanTitle}>
                    <span>2</span>
                    {tv("transportModeTitle")}
                </h1>
                <h3>{tv("transportModeSubtitle")}</h3>
                <p>{tv("transportModeDescription")}</p>
                <Dropdown
                    label=""
                    value={isBusFilter ? `bus_filter_${trackData.subType}` : trackData.type}
                    options={transportModeOptions}
                    onChange={handleTransportModeChange}
                    placeholder={tv("selectTransportMode")}
                    disabled={loading || isFrozen}
                    isMissing={showErrors && fieldErrors.transportMode}
                    loading={loading}
                    error={false}
                    isWithoutMargin
                />
            </div>

            {/* Step 3: Experience Type (only for categories with filters) */}
            {isCategory && categoryFilters.length > 0 && (
                <div className={stepStyles.stepDescription}>
                    <h1 className={stepStyles.textWithSpanTitle}>
                        <span>3</span>
                        {t("experienceType")}
                    </h1>
                    <h3>{t("whatTypeOfExperienceWouldYouLikeToCreate")}</h3>
                    <Dropdown
                        label=""
                        value={trackData.subType}
                        options={categoryFilters}
                        onChange={handleFilterChange}
                        placeholder={t("selectExperience")}
                        disabled={loading || isFrozen || hasOnlyOneFilter}
                        isMissing={showErrors && fieldErrors.filter}
                        loading={loading}
                        error={false}
                        isWithoutMargin
                    />

                    {/* Progression option for scan filters */}
                    {isScanFilter && (
                        <Dropdown
                            label=""
                            value={trackData.hasProgressionSystem ? "with" : "without"}
                            options={progressionOptions}
                            onChange={(value: string) => {
                                updateTrackData("hasProgressionSystem", value === "with");
                            }}
                            placeholder={t("selectProgressionType")}
                            disabled={loading || isFrozen}
                            loading={loading}
                            error={false}
                            isWithoutMargin
                        />
                    )}

                    {/* Trigger mode option for non-scan filters */}
                    {shouldShowTriggerMode && (
                        <Dropdown
                            label=""
                            value={trackData.defaultTriggerMode || "GPS"}
                            options={triggerModeOptions}
                            onChange={(value: string) => {
                                updateTrackData("defaultTriggerMode", value as "GPS" | "GPSAlert");
                            }}
                            placeholder={t("selectTriggerMode")}
                            disabled={loading || isFrozen}
                            loading={loading}
                            error={false}
                            isWithoutMargin
                        />
                    )}
                </div>
            )}

            <div className={`${stepStyles.stepWarning}${isFrozen ? ` ${stepStyles.stepWarningLocked}` : ""}`}>
                <h2>{t("productLanguageAndAudioTypeCannotBeChanged")}</h2>
                <p>{t("thisIsBecauseWeCustomizeTheAudioTourCreationProcessAccordingToYourInitialSelection")}</p>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    onClick={navigateToNext}
                    disabled={buttonDisabled()}
                    showError={showErrorMessage}
                />
            </div>
        </div>
    );
};

export default CategoryScreen;
